import { useEffect, useState } from "react";
import { Card, Button } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import moment from "moment";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import Table from "examples/Tables/Table";
import NoData from "containers/DataNotAvailBox";
import TableAction from "containers/table-action";
import DefaultModal from "containers/clientDetails/sub-components/modalContainer/default";
import DeleteModal from "containers/DeleteModal";
import CustomButton from "containers/CustomButton";
import { headerDesc, globaleDateTimeFormatter } from "utils/helper";
import {
  deleteGeneralFollowup,
  fetchGeneralFollowups,
  saveGeneralFollowup,
} from "services/generalFollowup";
import { toast } from "react-toastify";
import { TailSpin } from "react-loader-spinner";

const columns = [
  { name: "Date/Time", align: "left", minWidth: "180px" },
  { name: "Notes", align: "left", minWidth: "200px" },
  { name: "Action", align: "left", minWidth: "120px" },
];

export default function GeneralFollowup({ clientId, adminId, clientName, phoneNumber }) {
  const [entries, setEntries] = useState([]);
  const [loader, setLoader] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({
    followUpDate: moment().format("YYYY-MM-DDTHH:mm"),
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const reload = () =>
    fetchGeneralFollowups({ clientId })
      .then(setEntries)
      .catch(() => toast.error("Could not load follow-ups"));

  useEffect(() => {
    if (!clientId) return;
    setLoader(true);
    reload().finally(() => setLoader(false));
  }, [clientId]);

  const open = (mode, item = null) => {
    setModal({ mode, item });
    setForm(
      item
        ? {
            followUpDate: moment(item.followUpDate).format("YYYY-MM-DDTHH:mm"),
            notes: item.notes ?? "",
          }
        : { followUpDate: moment().format("YYYY-MM-DDTHH:mm"), notes: "" }
    );
  };

  const submit = async () => {
    if (!form.followUpDate || !form.notes?.trim()) {
      toast.error(!form.followUpDate ? "Please select date and time" : "Please enter notes");
      return;
    }
    try {
      setSaving(true);
      await saveGeneralFollowup({
        id: modal?.item?.id,
        adminId,
        clientId,
        clientName,
        phoneNumber,
        ...form,
      });
      toast.success(modal?.item ? "Follow-up updated" : "Follow-up created");
      setModal(null);
      reload();
    } catch (e) {
      toast.error(e?.message ?? "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const rows = entries.map((item) => ({
    "Date/Time": (
      <SuiTypography variant="button" fontWeight="medium" style={{ paddingLeft: 16 }}>
        {globaleDateTimeFormatter(item.followUpDate)}
      </SuiTypography>
    ),
    Notes: (
      <Tooltip title={item.notes ?? ""}>
        <SuiTypography variant="button" fontWeight="medium">
          {item.notes?.length > 80 ? `${item.notes.slice(0, 80)}...` : item.notes || "—"}
        </SuiTypography>
      </Tooltip>
    ),
    Action: (
      <TableAction
        editHandler={() => open("edit", item)}
        viewHandler={() => open("view", item)}
        deleteHandler={() => setDeleteId(item.id)}
      />
    ),
  }));

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <SuiBox display="flex" justifyContent="space-between" alignItems="center" px={3} pt={3} pb={1}>
          <SuiTypography variant="h6">General Follow-up</SuiTypography>
          <SuiButton component={Button} color="dark" variant="gradient" onClick={() => open("create")}>
            Create
          </SuiButton>
        </SuiBox>
        <SuiBox px={2} pb={3}>
          {loader ? (
            <SuiBox display="flex" justifyContent="center" py={4}>
              <TailSpin color="#17c1e8" height={40} width={40} />
            </SuiBox>
          ) : rows.length ? (
            <Table columns={columns} rows={rows} />
          ) : (
            <SuiBox py={4} display="flex" justifyContent="center">
              <NoData message="No follow-ups yet. Click Create to add one." />
            </SuiBox>
          )}
        </SuiBox>
      </Card>

      {modal && (
        <DefaultModal
          open
          {...headerDesc(modal.mode, "Follow-up", modal.item)}
          title="Follow-up"
          handleCloseModal={() => setModal(null)}
          mode={modal.mode}
          setMode={(mode) => setModal((m) => ({ ...m, mode }))}
        >
          <SuiBox pb={3} px={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SuiTypography mb={1} variant="h6">Date/Time*</SuiTypography>
                <SuiInput
                  type="datetime-local"
                  disabled={modal.mode === "view"}
                  readOnly={modal.mode === "view"}
                  value={form.followUpDate}
                  onChange={(e) => setForm({ ...form, followUpDate: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <SuiTypography mb={1} variant="h6">Notes*</SuiTypography>
                <SuiInput
                  multiline
                  rows={5}
                  disabled={modal.mode === "view"}
                  readOnly={modal.mode === "view"}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </Grid>
            </Grid>
            {modal.mode !== "view" && (
              <SuiBox display="flex" justifyContent="flex-end" mt={3}>
                <CustomButton
                  color="dark"
                  variant="gradient"
                  onClick={submit}
                  title={modal.item ? "Update" : "Create"}
                  loader={saving}
                  width={120}
                />
              </SuiBox>
            )}
          </SuiBox>
        </DefaultModal>
      )}

      {deleteId && (
        <DeleteModal
          title="Are you sure you want to delete this follow-up?"
          onDeleteHandler={async () => {
            await deleteGeneralFollowup({ id: deleteId });
            toast.success("Follow-up deleted");
            setDeleteId(null);
            reload();
          }}
          selectedItem={{ id: deleteId }}
          setSelectedItem={() => setDeleteId(null)}
          deleteModal
          setDeleteModal={(open) => !open && setDeleteId(null)}
        />
      )}
    </>
  );
}
